import { blobError, downloadBlob } from '@/lib/api/download'
import { mainEndpoints } from '@/lib/api/endpoints'
import { mainApi, mainHttp } from '@/lib/api/http'

import type {
  AuditLogQuery,
  AuditLogReport,
  InvoiceLinesReport,
  ItemSalesQuery,
  ItemSalesReport,
  ReportQuery,
  SalesRegisterReport,
  SalesSummaryQuery,
  SalesSummaryReport,
  SettlementReport,
} from '../types'

const reports = mainEndpoints.reports

/**
 * The query as it goes out.
 *
 * An empty outlet list is dropped rather than sent: absent means the caller's
 * whole scope, and `branchIds=` is a 400. Unset optionals are left undefined,
 * which axios omits, so "every channel" is a missing key rather than an empty
 * string the DTO would reject.
 */
function params<T extends ReportQuery>({ branchIds, ...rest }: T): Record<string, unknown> {
  return { ...rest, ...(branchIds?.length ? { branchIds } : {}) }
}

export async function fetchSalesSummary(query: SalesSummaryQuery): Promise<SalesSummaryReport> {
  return await mainApi.get<SalesSummaryReport>(reports.salesSummary, { params: params(query) })
}

export async function fetchSalesRegister(query: ReportQuery): Promise<SalesRegisterReport> {
  return await mainApi.get<SalesRegisterReport>(reports.salesRegister, { params: params(query) })
}

export async function fetchItemSales(query: ItemSalesQuery): Promise<ItemSalesReport> {
  return await mainApi.get<ItemSalesReport>(reports.itemSales, { params: params(query) })
}

export async function fetchInvoiceLines(query: ReportQuery): Promise<InvoiceLinesReport> {
  return await mainApi.get<InvoiceLinesReport>(reports.invoiceLines, { params: params(query) })
}

export async function fetchSettlement(query: ReportQuery): Promise<SettlementReport> {
  return await mainApi.get<SettlementReport>(reports.settlement, { params: params(query) })
}

export async function fetchAuditLog(query: AuditLogQuery): Promise<AuditLogReport> {
  return await mainApi.get<AuditLogReport>(reports.auditLog, { params: params(query) })
}

/**
 * Which workbook each report exports to.
 *
 * The file name matches the one the server writes into Content-Disposition —
 * `Sales-Summary_2026-10-01_to_2026-10-14.xlsx` — so a file saved from here and
 * one fetched by hand from the API are recognisably the same report. It is
 * rebuilt here rather than read off the header because a cross-origin response
 * only exposes the headers the API chooses to, and that one is not among them.
 */
export const ReportExport = {
  SalesSummary: { url: reports.salesSummaryExport, name: 'Sales-Summary' },
  SalesRegister: { url: reports.salesRegisterExport, name: 'Sales-Register' },
  ItemSales: { url: reports.itemSalesExport, name: 'Item-Sales' },
  InvoiceLines: { url: reports.invoiceLinesExport, name: 'Invoice-Lines' },
  Settlement: { url: reports.settlementExport, name: 'Settlement' },
  AuditLog: { url: reports.auditLogExport, name: 'Audit-Log' },
} as const

export type ReportExport = (typeof ReportExport)[keyof typeof ReportExport]

/** A workbook fetched from the server, not yet saved. */
export interface ReportWorkbook {
  blob: Blob
  fileName: string
}

/**
 * The report as an Excel workbook, fetched but not saved.
 *
 * Server-built rather than written from the rows on screen, which is the
 * opposite trade to the close-outs CSV and deliberate: these reports answer
 * every line of the period in one read, so the server's file holds exactly what
 * the screen pages through — and it holds it as real date and number cells,
 * rounded to the cent, which is what a merchant reconciling in Excel needs and
 * what a CSV cannot carry.
 *
 * Returned rather than downloaded so the merchant can look at it first; the
 * preview dialog calls `saveReportWorkbook` once they confirm.
 */
export async function fetchReportWorkbook(
  report: ReportExport,
  query: ReportQuery,
): Promise<ReportWorkbook> {
  let data: Blob

  try {
    ;({ data } = await mainHttp.get<Blob>(report.url, {
      params: params(query),
      responseType: 'blob',
    }))
  } catch (error) {
    throw await blobError(error)
  }

  return { blob: data, fileName: `${report.name}_${query.from}_to_${query.to}.xlsx` }
}

export function saveReportWorkbook({ blob, fileName }: ReportWorkbook): void {
  downloadBlob(blob, fileName)
}
